import React, { useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { AnyObject } from 'final-form';
import useFetch from 'use-http';

interface ISectionBillingDialogProps {
    dialogName: string;
    handleReport?: (data: AnyObject) => void;
    hideDialog: () => void;
    // When the dialog is opened from a page that already has a client/project selected (e.g. a
    // Client/Project detail page), pass them here and the dialog skips its own Client/Project
    // inputs and uses these instead. When opened from the Report Dashboard there's no such context,
    // so leaving these unset falls back to rendering editable Client No./Project No. fields.
    clientNumber?: string | number;
    projectNumber?: string | number;
}

const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '9rem', marginRight: '1rem' };
const fieldStyle: React.CSSProperties = { width: '10rem' };

// The browser's <a download> attribute (used below) wins over any server-side Content-Disposition
// header, so the filename has to be picked here, not just in ReportsController.
// GetSectionBillingReport - same reasoning as ContractorsBilledDialog.tsx's REPORT_FILE_NAMES.
const REPORT_FILE_NAMES: Record<string, string> = {
    SectionUnitList: 'SectionBilling.xlsx',
    EasternSection: 'EasternBilling.xlsx',
    WTexasSection: 'WestTexasBilling.xlsx'
};

const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// "Build Excel File" screen - Report Type poplist (Section Billing/Eastern Billing/West Texas
// Billing) picks which already-deployed SSRS report renders (SectionUnitList/EasternSection/
// WTexasSection - see ReportService.GetSectionBillingReportFile), all three sharing the same
// Client No.|Project No. parameter shape. Excel-only, same fixed-format convention as
// ContractorsBilledDialog.tsx. The source screen's Filename field has no browser equivalent -
// dropped in favor of a fixed per-report-type auto-download, same as every other converted report
// dialog in this repo.
const SectionBillingDialog = (props: ISectionBillingDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { post, response } = useFetch();
    const [loading, setLoading] = useState<boolean>(false);
    const hasClientProp = props.clientNumber !== undefined && props.clientNumber !== null;
    const hasProjectProp = props.projectNumber !== undefined && props.projectNumber !== null;

    const OKClicked = async () => {
        const data = getValues();
        const reportOptions = data?.reportOptions;
        const reportName = reportOptions?.reportName || 'SectionUnitList';
        const clientNumber = hasClientProp ? String(props.clientNumber) : reportOptions?.clientNumber;
        const projectNumber = hasProjectProp ? String(props.projectNumber) : reportOptions?.projectNumber;

        setLoading(true);

        try {
            await post('/v1.0/reports/section-billing', {
                reportName,
                clientNumber,
                projectNumber
            });

            if (response.ok) {
                const blob = await response.blob();
                downloadBlobResponseAsFile(blob, REPORT_FILE_NAMES[reportName] || 'SectionBilling.xlsx');
            }
        } catch (e) {
            console.log('Unable to generate Section Billing report.', e);
        }

        setLoading(false);
        props.handleReport?.({ ...reportOptions, clientNumber, projectNumber });
        props.hideDialog();
    };

    return (
        <Dialog title="Build Excel File" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '32%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '90%' }}>
                    <RHFSelect
                        label="Report Type"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.reportName"
                        options={[
                            { value: 'SectionUnitList', label: 'Section Billing' },
                            { value: 'EasternSection', label: 'Eastern Billing' },
                            { value: 'WTexasSection', label: 'West Texas Billing' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="SectionUnitList"
                        style={fieldStyle}
                    />
                    {!hasClientProp && (
                        <RHFInputBox
                            label="Client No."
                            labelOnLeft={true}
                            labelStyle={labelStyle}
                            name="reportOptions.clientNumber"
                            register={register}
                            errors={errors}
                            style={fieldStyle}
                        />
                    )}
                    {!hasProjectProp && (
                        <RHFInputBox
                            label="Project No."
                            labelOnLeft={true}
                            labelStyle={labelStyle}
                            name="reportOptions.projectNumber"
                            register={register}
                            errors={errors}
                            style={fieldStyle}
                        />
                    )}
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '1.5rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={loading ? 'Generating' : 'OK'}
                    disabled={loading}
                    loading={loading}
                    onClick={() => !loading && OKClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={'Cancel'}
                    disabled={loading}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default SectionBillingDialog;



// WEBPACK FOOTER //
// ./src/components/reports/dialogs/SectionBillingDialog.tsx